/**
 * Overlapping circular patient initials — used on multi-patient review.
 */
export function patientInitial(name, fallbackIndex = 0) {
  const trimmed = String(name || '').trim();
  if (!trimmed) return String(fallbackIndex + 1);
  // Prefer Latin/Arabic letter; skip "Patient 1" → use number from label if needed
  const match = trimmed.match(/[\p{L}]/u);
  if (match) return match[0].toLocaleUpperCase();
  const digit = trimmed.match(/\d+/);
  if (digit) return digit[0].slice(-1);
  return String(fallbackIndex + 1);
}

export default function PatientAvatarStack({
  patients = [],
  activeIndex = null,
  completedIndexes = [],
  onSelect,
  size = 'md',
  className = '',
}) {
  if (!patients.length) return null;

  const sizeClass =
    size === 'lg' ? 'h-12 w-12 text-base' : size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  const overlapClass = size === 'lg' ? '-ml-3' : size === 'sm' ? '-ml-2' : '-ml-2.5';

  return (
    <div
      className={`flex items-center ${className}`}
      role="tablist"
      aria-label="Patients in this review"
    >
      {patients.map((patient, index) => {
        const name = patient?.name || patient?.patient_name || `Patient ${index + 1}`;
        const initial = patientInitial(name, index);
        const isActive = activeIndex === index;
        const isCompleted = completedIndexes.includes(index);
        const clickable = typeof onSelect === 'function';

        return (
          <button
            key={patient?.id || `${name}-${index}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-label={name}
            title={name}
            disabled={!clickable}
            onClick={() => onSelect?.(index)}
            className={`relative inline-flex ${sizeClass} shrink-0 items-center justify-center rounded-full border-2 font-bold text-white shadow-sm transition-transform ${
              index === 0 ? '' : overlapClass
            } ${
              isActive
                ? 'z-20 scale-110 border-white ring-2 ring-primary ring-offset-1'
                : 'z-10 border-white'
            } ${clickable ? 'cursor-pointer hover:z-30 hover:scale-105' : 'cursor-default'} ${
              isCompleted ? 'bg-success' : 'bg-gradient-to-b from-[#3B82F6] to-primary'
            }`}
            style={{ zIndex: isActive ? 30 : 10 + index }}
          >
            {initial}
          </button>
        );
      })}
    </div>
  );
}
